// EC-849-Create a custom redux store to store the data
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Grid from "@insite/mobius/Icons/Grid";
import List from "@insite/mobius/Icons/List";
import Table from "@insite/mobius/Icons/Table";
import CatalogView from "@WhiteCap/Icons/CatalogView";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import setView from "@WhiteCap/Handlers/ProductGrouping/SetViewHandler";
import { ViewType } from "@WhiteCap/Store/Reducer/WhiteCapReducer/ProductListViewReducer";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";


interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => ({
    isSearchPage: state.pages.productList.isSearchPage,
    loaded: !!getProductListDataView(state).value,
    isCatalogViewEnabled: state.context.settings.properties.enableCatalogView === "True",
    // EC-849-Create a custom redux store to store the data
    // use custom Product list view 
    view: state.WhiteCap.view.view,
});

const mapDispatchToProps = {
    // EC-849-Create a custom redux store to store the data
    // use custom Product list view 
    setView,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListViewSelectStyles {
    wrapper?: InjectableCss;
    listViewClickable?: ClickablePresentationProps;
    listViewIcon?: IconPresentationProps;
    gridViewClickable?: ClickablePresentationProps;
    gridViewIcon?: IconPresentationProps;
    tableViewClickable?: ClickablePresentationProps;
    tableViewIcon?: IconPresentationProps;
    catalogViewIcon?: IconPresentationProps;
}

export const viewSelectStyles: ProductListViewSelectStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding-top: 5px;
            float: right;
        `,
    },
    listViewClickable: {
        css: css`
            margin-right: 20px;
        `,
    },
    gridViewClickable: {
        css: css`
            margin-right: 20px;
        `,
    },
    listViewIcon: {
        src: List,
        size: 30,
    },
    gridViewIcon: {
        src: Grid,
        size: 30,
    },
    tableViewIcon: {
        src: Table,
        size: 26,
    },
    catalogViewIcon:{
        src: CatalogView,
        size: 30,
    }
};

const styles = viewSelectStyles;

class ProductListViewSelect extends React.Component<Props> {
    componentDidMount() {
        const view = getCookie("ProductList-View");
        
        // If the catalog view is disabled and the current view is "Catalog", reset to the default view
        if (view === "Catalog" && !this.props.isCatalogViewEnabled) {
            const defaultView = "List";
            setCookie("ProductList-View", defaultView);
            this.props.setView({ view: defaultView as ViewType }); // Ensure the component updates
        } else if (view && this.props.view !== view) {
            this.props.setView({ view: view as ViewType });
        }
    }

    componentDidUpdate(prevProps: Props) {
        // If the user is on catalog view and initiates a search, reset to default view
        if (this.props.view === "Catalog" && this.props.isSearchPage && !prevProps.isSearchPage) {
            this.resetToDefaultView();
        }
    }

    resetToDefaultView = () => {
        const defaultView: ViewType = "List"; // Set default view
        setCookie("ProductList-View", defaultView);
        this.props.setView({ view: defaultView });
    };

    render() {
        const { loaded, view, setView, isCatalogViewEnabled, isSearchPage } = this.props;

        if (!loaded) {
            return null;
        }

        const clickListViewHandler = () => {
            changeView("List");
        };

        const clickGridViewHandler = () => {
            changeView("Grid");
        };

        const clickTableViewHandler = () => {
            changeView("Table");
        };

        {/* EC-848:- Click Handler to Change the view */}
        const clickCatalogViewHandler = () => {
            changeView("Catalog");
        }

        const changeView = (view: ViewType) => {
            setCookie("ProductList-View", view);
            setView({ view });
        };

       
        return (
            <StyledWrapper {...styles.wrapper}>
                {/* EC-848:- Adding New Catalog View on PLP */}
                {isCatalogViewEnabled && !isSearchPage && (
                    <Clickable
                        data-test-selector="viewSelectCatalog"
                        {...styles.gridViewClickable}
                        onClick={clickCatalogViewHandler}
                    >
                        <VisuallyHidden>{translate("Product Catalog View")}</VisuallyHidden>
                        <Icon {...styles.catalogViewIcon} color={view === "Catalog" ? "primary" : undefined} />
                    </Clickable>

                )}
                <Clickable
                    data-test-selector="viewSelectList"
                    {...styles.listViewClickable}
                    onClick={clickListViewHandler}
                >
                    <VisuallyHidden>{translate("Product List View")}</VisuallyHidden>
                    <Icon {...styles.listViewIcon} color={view === "List" ? "primary" : undefined} />
                </Clickable>
                <Clickable
                    data-test-selector="viewSelectGrid"
                    {...styles.gridViewClickable}
                    onClick={clickGridViewHandler}
                >
                    <VisuallyHidden>{translate("Product Grid View")}</VisuallyHidden>
                    <Icon {...styles.gridViewIcon} color={view === "Grid" ? "primary" : undefined} />
                </Clickable>
                <Clickable
                    data-test-selector="viewSelectTable"
                    {...styles.gridViewClickable}
                    onClick={clickTableViewHandler}
                >
                    <VisuallyHidden>{translate("Product Table View")}</VisuallyHidden>
                    <Icon {...styles.tableViewIcon} color={view === "Table" ? "primary" : undefined} />
                </Clickable>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListViewSelect),
    definition: {
        group: "Product List",
        displayName: "View Select",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
